import React, { memo, useCallback } from 'react';
import { useIntl } from 'react-intl';

import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { Box, Typography } from '@mui/material';
import { format } from 'date-fns';
import { ActivePromotionDetails } from 'promotions/components/ActivePromotionsGrid/ActivePromotionDetailsComponent';
import CashIcon from 'promotions/components/ActivePromotionsGrid/icons/CashIcon';
import { ViewDetailsButton } from 'promotions/components/ActivePromotionsGrid/VideDetailsButtonComponent';
import { PromotionDetails } from 'promotions/types';

const styles = {
  card: (isBestUserCampaign?: boolean) => {
    return {
      minWidth: '396px',
      display: 'flex',
      flexDirection: 'column',
      padding: '24px',
      background: isBestUserCampaign ? '#EBECFF' : '#F4F5F6',
      borderRadius: '16px',
      height: '100%',
      minHeight: '384px',
      boxSizing: 'border-box',
    } as const;
  },
  info: {
    flex: '1 1 auto',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  title: {
    alignSelf: 'stretch',
    fontSize: '1.25rem',
  },
  titleText: {
    flexDirection: 'column',
    justifyContent: 'center',
    minHeight: '3rem',
    gap: '100%',
  },
} as const;
interface ActivePromotionsProps {
  strings: {
    promotionCard: {
      viewDetails: string;
      tooltipInfo: string;
    };
  };
  promotionDetail: PromotionDetails;
  promotionClicked: (id: number) => void;
}
export const ActivePromotion: React.FC<ActivePromotionsProps> = memo(
  ({ strings, promotionDetail, promotionClicked }) => {
    const intl = useIntl();
    const onClickHandler = useCallback(() => {
      promotionClicked(promotionDetail.id);
    }, [promotionDetail.id, promotionClicked]);
    const startDate = format(promotionDetail.startDate, 'MMM dd, yyyy');
    const endDate = format(promotionDetail.endDate, 'MMM dd, yyyy');
    return (
      <Box sx={styles.card(promotionDetail.isBestUserCampaign)}>
        <Box sx={styles.info}>
          <Box sx={styles.title}>
            <Typography variant="h6" color="textPrimary" sx={styles.titleText}>
              {promotionDetail.promotionName}
            </Typography>
          </Box>
          <Box>
            <ActivePromotionDetails
              icon={CalendarTodayOutlinedIcon}
              title={intl.formatMessage({
                id: 'components.activePromotionsGrid.statsTitles.duration',
              })}
              value={`${startDate} - ${endDate}`}
            />
            <ActivePromotionDetails
              icon={PersonOutlineOutlinedIcon}
              title={intl.formatMessage({
                id: 'components.PromotionStats.newCustomers',
              })}
              value={`${Math.floor(Number(promotionDetail.newUsers))}`}
            />
            <ActivePromotionDetails
              icon={CashIcon}
              title={intl.formatMessage({
                id: 'components.activePromotionsGrid.statsTitles.additionalSales',
              })}
              value={promotionDetail.gmvImpact}
            />
            <ActivePromotionDetails
              icon={AccountBalanceWalletOutlinedIcon}
              title={intl.formatMessage({
                id: 'components.activePromotionsGrid.statsTitles.spend',
              })}
              value={promotionDetail.totalSpend}
            />
          </Box>
        </Box>
        <ViewDetailsButton strings={strings.promotionCard} onClick={onClickHandler} />
      </Box>
    );
  },
);
